import {memoize} from 'lodash';
import {DateTime} from 'luxon';
import {ReactNode} from 'react';

import {formatTime, TimeFormatsEnum} from '../../../libs/shared/core/src/helpers/datetime/dateTimeHelpers';
import {Localizer} from '../../../libs/shared/core/src/helpers/internationalization/localizeTypes';
import {RendererOf} from '../../../libs/shared/core/src/helpers/react/reactHelpers';
import {PaletteColorsEnum} from '../../../libs/shared/core/src/models/api/paletteModel';
import {IconName} from '../../../libs/shared/ui/src/components/icon/icon';
import {VisualSizesEnum} from '../../../libs/shared/ui/src/styles/commonStyles';
import {UserTheme} from '../../../libs/shared/ui/src/theme/themeTypes';
import {
  AnalyticsDataPoint,
  AnalyticsDataPointTypesEnum,
  isAccountDataPoint,
  isCardDataPoint,
  isContactDataPoint,
  isInboxDataPoint,
  isNumericDataPoint,
  isNumericDataPointType,
  isStringDataPoint,
  isTagDataPoint,
  isTeamDataPoint,
  isTeammateDataPoint,
} from '../../models/api/analyticsMetricsModel';
import {HighlightTypesEnum} from '../../models/api/highlightBaseModel';
import {renderAccountValue} from './metricRenderers/accountRenderer';
import {renderAvatarValue} from './metricRenderers/avatarRenderer';
import {renderDurationValue} from './metricRenderers/durationMetricRenderer';
import {renderInboxValue} from './metricRenderers/inboxRenderer';
import {renderNumericalValue} from './metricRenderers/numericalMetricRenderer';
import {renderPercentageValue} from './metricRenderers/percentMetricRenderer';
import {renderProgressValue} from './metricRenderers/progressMetricRenderer';
import {renderTagValue} from './metricRenderers/tagRenderer';
import {renderTeammateValue} from './metricRenderers/teammateRenderer';
import {renderTeamValue} from './metricRenderers/teamRenderer';
import {renderTextValue} from './metricRenderers/textMetricRenderer';

/*
 * Metric value renderers.
 */

export type MetricNumericalRenderer<T = ReactNode> = ({
  value,
  shouldRenderOnlyWholeNumbers,
  isRawVariation,
  isCompact,
  compactThreshold,
  isZeroValid,
  shouldRoundDownToHundred,
}: {
  value: number;
  shouldRenderOnlyWholeNumbers?: boolean;
  isRawVariation?: boolean;
  isCompact?: boolean;
  compactThreshold?: number;
  isZeroValid?: boolean;
  shouldRoundDownToHundred?: boolean;
}) => T;
export type MetricTextRenderer = RendererOf<{value: string}>;
export type MetricResourceRenderer = ({
  value,
  id,
  size,
}: {
  value: string;
  id: number;
  size?: VisualSizesEnum;
}) => ReactNode;

export type HighlightData =
  | {
      type: HighlightTypesEnum.NONE;
    }
  | {
      type: HighlightTypesEnum.EMOJI;
      highlightEmoji: string;
    }
  | {
      type: HighlightTypesEnum.COLOR;
      highlightColor: PaletteColorsEnum;
    };

export type MetricTagRenderer = (p: {
  value: string;
  isArchived: boolean;
  highlight: HighlightData;
}) => ReactNode;
export type MetricTeamRenderer = ({id, value}: {id?: number; value?: string}) => ReactNode;
export type MetricAccountRenderer = ({
  id,
  value,
  avatarUrl,
}: {
  id?: number;
  value?: string;
  avatarUrl?: string;
}) => ReactNode;
export type MetricAvatarRenderer = ({value, avatarUrl}: {value: string; avatarUrl?: string}) => ReactNode;

// Type overloads - based on the input type a different renderer is returned.
export function getMetricRenderer(
  dataPointType:
    | AnalyticsDataPointTypesEnum.NUMERICAL
    | AnalyticsDataPointTypesEnum.PERCENTAGE
    | AnalyticsDataPointTypesEnum.PROGRESS
    | AnalyticsDataPointTypesEnum.DURATION,
): MetricNumericalRenderer;

export function getMetricRenderer(dataPointType: AnalyticsDataPointTypesEnum.TEXT): MetricTextRenderer;
export function getMetricRenderer(dataPointType: AnalyticsDataPointTypesEnum.TAG): MetricTagRenderer;
export function getMetricRenderer(dataPointType: AnalyticsDataPointTypesEnum.TEAM): MetricTeamRenderer;
export function getMetricRenderer(dataPointType: AnalyticsDataPointTypesEnum.ACCOUNT): MetricAccountRenderer;
export function getMetricRenderer(dataPointType: AnalyticsDataPointTypesEnum.CONTACT): MetricAvatarRenderer;
export function getMetricRenderer(dataPointType: AnalyticsDataPointTypesEnum.CARD): MetricAvatarRenderer;

export function getMetricRenderer(
  dataPointType: AnalyticsDataPointTypesEnum.TEAMMATE | AnalyticsDataPointTypesEnum.INBOX,
): MetricResourceRenderer;

/**
 * Based on a type will return the appropriate metric renderer which can then be directly called.
 * Useful when you need to attach the renderer and use it later.
 */
export function getMetricRenderer(dataPointType: AnalyticsDataPointTypesEnum) {
  switch (dataPointType) {
    case AnalyticsDataPointTypesEnum.NUMERICAL:
      return renderNumericalValue;

    case AnalyticsDataPointTypesEnum.PERCENTAGE:
      return renderPercentageValue;

    case AnalyticsDataPointTypesEnum.PROGRESS:
      return renderProgressValue;

    case AnalyticsDataPointTypesEnum.DURATION:
      return renderDurationValue;

    case AnalyticsDataPointTypesEnum.TAG:
      return renderTagValue;

    case AnalyticsDataPointTypesEnum.TEAMMATE:
      return renderTeammateValue;

    case AnalyticsDataPointTypesEnum.INBOX:
      return renderInboxValue;

    case AnalyticsDataPointTypesEnum.ACCOUNT:
      return renderAccountValue;

    case AnalyticsDataPointTypesEnum.CONTACT:
    case AnalyticsDataPointTypesEnum.CARD:
      return renderAvatarValue;

    case AnalyticsDataPointTypesEnum.TEAM:
      return renderTeamValue;

    case AnalyticsDataPointTypesEnum.TEXT:
    default:
      return renderTextValue;
  }
}

/**
 * Will directly render a metric data based on its type and model. Used for Table cells and List items.
 */
export function renderFormattedMetricData(
  dataPoint: AnalyticsDataPoint,
  dataPointType: AnalyticsDataPointTypesEnum,
) {
  // {value: number} case.
  if (isNumericDataPointType(dataPointType) && isNumericDataPoint(dataPoint)) {
    return getMetricRenderer(dataPointType)({value: dataPoint.value});
  }

  // {value: string, id: number} case.
  if (dataPointType === AnalyticsDataPointTypesEnum.TEAMMATE && isTeammateDataPoint(dataPoint)) {
    return getMetricRenderer(dataPointType)({value: dataPoint.value, id: dataPoint.id});
  }

  if (dataPointType === AnalyticsDataPointTypesEnum.INBOX && isInboxDataPoint(dataPoint)) {
    return getMetricRenderer(dataPointType)({value: dataPoint.value, id: dataPoint.id});
  }

  if (dataPointType === AnalyticsDataPointTypesEnum.TAG && isTagDataPoint(dataPoint)) {
    const {value, isArchived, highlight} = dataPoint;
    return getMetricRenderer(dataPointType)({
      value,
      isArchived,
      highlight,
    });
  }

  if (dataPointType === AnalyticsDataPointTypesEnum.TEAM && isTeamDataPoint(dataPoint)) {
    return getMetricRenderer(dataPointType)({
      id: dataPoint.id,
      value: dataPoint.value,
    });
  }

  // {value: string, id: number, avatarUrl: string} case.
  if (dataPointType === AnalyticsDataPointTypesEnum.ACCOUNT && isAccountDataPoint(dataPoint)) {
    return getMetricRenderer(dataPointType)({
      id: dataPoint.id,
      value: dataPoint.value,
      avatarUrl: dataPoint.avatarUrl,
    });
  }

  if (dataPointType === AnalyticsDataPointTypesEnum.CARD && isCardDataPoint(dataPoint)) {
    return getMetricRenderer(dataPointType)({
      value: dataPoint.value,
      avatarUrl: dataPoint.avatarUrl,
    });
  }

  if (dataPointType === AnalyticsDataPointTypesEnum.CONTACT && isContactDataPoint(dataPoint)) {
    return getMetricRenderer(dataPointType)({
      value: dataPoint.value,
      avatarUrl: dataPoint.avatarUrl,
    });
  }

  // {value: string} case.
  if (dataPointType === AnalyticsDataPointTypesEnum.TEXT && isStringDataPoint(dataPoint)) {
    return getMetricRenderer(dataPointType)({value: dataPoint.value});
  }

  return dataPoint.value;
}

/*
 * Weekday / hour helpers.
 */

const weekDays: Record<number, string> = {
  1: 'Mon',
  2: 'Tue',
  3: 'Wed',
  4: 'Thu',
  5: 'Fri',
  6: 'Sat',
  7: 'Sun',
};

const weekDaysFull: Record<number, string> = {
  1: 'Monday',
  2: 'Tuesday',
  3: 'Wednesday',
  4: 'Thursday',
  5: 'Friday',
  6: 'Saturday',
  7: 'Sunday',
};

export const renderWeekDay = (day: number) => weekDays[day];
export const renderWeekDayFull = (day: number) => weekDaysFull[day];

export const renderHourWithoutMinutes = (localize: Localizer, hour: number) =>
  formatTime(localize, TimeFormatsEnum.H12, DateTime.fromFormat(hour.toString(), 'H')).replace(':00', '');

/*
 * Numeric variations.
 */

interface TrendConfig {
  color: string;
  icon: IconName;
}

enum AnalyticsVariationsTrendsEnum {
  UP = 'up',
  NEUTRAL = 'neutral',
  DOWN = 'down',
}

type TrendConfigBuilder = (theme: UserTheme) => Record<AnalyticsVariationsTrendsEnum, TrendConfig>;
const buildTrendConfigs: TrendConfigBuilder = memoize((theme: UserTheme) => ({
  [AnalyticsVariationsTrendsEnum.DOWN]: {
    color: theme.palette[PaletteColorsEnum.PURPLE].shade50,
    icon: 'arrowDownAngleSmall',
  },
  [AnalyticsVariationsTrendsEnum.NEUTRAL]: {
    color: theme.greys.shade70,
    icon: 'minusSmall',
  },
  [AnalyticsVariationsTrendsEnum.UP]: {
    color: theme.palette[PaletteColorsEnum.TEAL].shade50,
    icon: 'arrowUpAngleSmall',
  },
}));

const getTrendFromVariation = (variation: number): AnalyticsVariationsTrendsEnum => {
  if (variation < 0) {
    return AnalyticsVariationsTrendsEnum.DOWN;
  }

  if (variation > 0) {
    return AnalyticsVariationsTrendsEnum.UP;
  }

  return AnalyticsVariationsTrendsEnum.NEUTRAL;
};

const getTrendConfig = (trend: AnalyticsVariationsTrendsEnum, theme: UserTheme) =>
  buildTrendConfigs(theme)[trend];

export const getVariationConfig = (value: number, previousValue: number, theme: UserTheme) => {
  const variation = value - previousValue;

  return {
    variation,
    variationPct: previousValue !== 0 ? variation / previousValue : undefined,
    ...getTrendConfig(getTrendFromVariation(variation), theme),
  };
};

export const maybeGetPreviousPeriodFormatter = (
  previousPeriodStart?: DateTime,
  previousPeriodEnd?: DateTime,
) => {
  if (!previousPeriodStart || !previousPeriodEnd) {
    return null;
  }

  return `${previousPeriodStart.toFormat('MMM d, yyyy')} - ${previousPeriodEnd.toFormat('MMM d, yyyy')}`;
};

export const getDonutArcIndexColor = (
  index: number,
  theme: UserTheme,
  color: PaletteColorsEnum = PaletteColorsEnum.BLUE,
) => {
  switch (index) {
    case 0:
      return theme.palette[color].shade10;
    case 1:
      return theme.palette[color].shade20;
    case 2:
      return theme.palette[color].shade30;
    case 3:
      return theme.palette[color].shade40;
    case 4:
      return theme.palette[color].shade50;
    default:
      // shouldn't ever happen, but handle gracefully just in case
      return theme.greys.shade50;
  }
};
